import type { NgZone } from '@angular/core';
import { Observable } from 'rxjs';
import { share } from 'rxjs/operators';
import type { Socket } from 'socket.io-client';
import { io } from 'socket.io-client';
import type { IEventPayload, ResourceId, SocketIoConfig } from './socket-io.model';

export class SocketIo {
  private subscribersCounter: Record<string, number> = {};
  private eventObservables$: Record<string, Observable<unknown>> = {};

  private socket: Socket;

  constructor(
    private config: SocketIoConfig,
    private ngZone: NgZone
  ) {}

  connect(featurePath?: string) {
    let url: string;
    if (featurePath) {
      url = `${this.config.url}/${featurePath}`;
    } else {
      url = this.config.url;
    }

    // creates and connects
    this.ngZone.runOutsideAngular(() => {
      this.socket = io(url, this.config.options);
    });
  }

  disconnect() {
    this.socket.disconnect();
  }

  send(event: string, ...args: unknown[]) {
    this.socket.emit(event, ...args);
  }

  join(resource: string, ids: ResourceId[] | Record<string, unknown> = []) {
    this.socket.emit('handshake', { resource, ids });
  }

  leave(resource: string, ids: ResourceId[] | Record<string, unknown> = []) {
    this.socket.emit('leave', { resource, ids });
  }

  unsubscribe() {
    this.socket.emit('leave-all');
  }

  acknowledge(ids: string[]) {
    this.socket.emit('acknowledgement', ids);
  }

  on<T = unknown>(event: string, cb: (payload: IEventPayload<T>) => void) {
    this.socket.on(event, (payload: IEventPayload<T>) => {
      this.ngZone.run(() => {
        cb(payload);
      });

      // ack packet
      if (payload?.ID) {
        this.acknowledge([payload.ID]);
      }
    });
  }

  // biome-ignore lint/suspicious/noExplicitAny: existing any, to be typed incrementally
  off(event: string, listener?: (...args: any[]) => void) {
    if (!listener) {
      // Remove all listeners for that event
      return this.socket.off(event);
    }

    // Removes the specified listener from the listener array for the event named
    return this.socket.off(event, listener);
  }

  fromEvent$<T = unknown>(event: string): Observable<T> {
    if (!this.subscribersCounter[event]) {
      this.subscribersCounter[event] = 0;
    }
    this.subscribersCounter[event]++;

    // Create a new observable if it doesn't exist
    if (!this.eventObservables$[event]) {
      this.eventObservables$[event] = new Observable<T>((obs) => {
        const listener = (payload: IEventPayload<T>) => {
          this.ngZone.run(() => {
            obs.next(payload.message);
          });

          // ack packet
          if (payload?.ID) {
            this.acknowledge([payload.ID]);
          }
        };

        this.socket.on(event, listener);

        return () => {
          this.subscribersCounter[event]--;
          if (this.subscribersCounter[event] === 0) {
            this.socket.off(event, listener);
            delete this.eventObservables$[event];
          }
        };
      }).pipe(share());
    }

    // Reuse the existing observable
    return this.eventObservables$[event] as Observable<T>;
  }
}
